import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import type {
  ClusterDetail,
  ClusterStats,
  ManagedCluster,
} from '@internal/plugin-cluster-common';

export interface ClusterApi {
  list(): Promise<ManagedCluster[]>;
  stats(): Promise<ClusterStats>;
  get(namespace: string, name: string): Promise<ClusterDetail>;
  downloadKubeconfig(namespace: string, name: string): Promise<void>;
}

export const clusterApiRef = createApiRef<ClusterApi>({
  id: 'plugin.cluster.service',
});

export class ClusterClient implements ClusterApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async base(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('cluster');
  }

  /** Unwraps Backstage's error envelope so the real reason reaches the UI. */
  private async fail(res: Response): Promise<never> {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.error?.message) detail = body.error.message;
    } catch {
      // Non-JSON body; the status line is all we have.
    }
    throw new Error(detail);
  }

  private async request<T>(path: string): Promise<T> {
    const res = await this.fetchApi.fetch(`${await this.base()}${path}`);
    if (!res.ok) await this.fail(res);
    return (await res.json()) as T;
  }

  async list() {
    const r = await this.request<{ items: ManagedCluster[] }>('/clusters');
    return r.items;
  }

  async stats() {
    return this.request<ClusterStats>('/stats');
  }

  async get(namespace: string, name: string) {
    return this.request<ClusterDetail>(
      `/clusters/${encodeURIComponent(namespace)}/${encodeURIComponent(name)}`,
    );
  }

  /**
   * Fetched rather than linked: the endpoint is user-scoped, so it needs the
   * Backstage token that only `fetchApi` attaches. A plain anchor would arrive
   * unauthenticated.
   */
  async downloadKubeconfig(namespace: string, name: string) {
    const res = await this.fetchApi.fetch(
      `${await this.base()}/clusters/${encodeURIComponent(
        namespace,
      )}/${encodeURIComponent(name)}/kubeconfig`,
    );
    if (!res.ok) await this.fail(res);

    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = `${name}-kubeconfig.yaml`;
    a.click();
    URL.revokeObjectURL(url);
  }
}
